import React, { useCallback } from 'react';
import { Box, Flex, PopoverV2, TextV2, useDisclosure } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { ChevronDown } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { DropdownButton } from '@apps/stays/components/DropdownButton';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { SortOptionsFacet } from './SortOptionsFacet';
import { useSortOptions } from '@apps/stays/AccommodationsConsole/hooks/useSortOptions';
import type { SortOption } from '@shared/constants/accommodations';
import { useListingsPanelTelemetry } from '../ListingsPanel.telemetry';

type SortComponentProps = Readonly<{
  selectedValue: SortOption;
  onChange: (value: SortOption) => void;
  includeCouplesPicks: boolean;
  includeFundSavings?: boolean;
  accentColor?: string;
  /** `pill` draws the toolbar pill, reading "Sort: <option>" as the hotel pick's design does. */
  appearance?: 'default' | 'pill';
}>;

export const SortComponent: React.FC<SortComponentProps> = ({ selectedValue, onChange, includeCouplesPicks, includeFundSavings, accentColor, appearance = 'default' }) => {
  const { tq } = useTranslation('accommodationsConsole');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isMobileOrTablet = useIsMobileOrTablet();
  const { getSelectedOptionLabel, sortTrans } = useSortOptions(includeCouplesPicks, includeFundSavings);
  const telemetry = useListingsPanelTelemetry();

  const handleSortChange = useCallback(
    (value: SortOption) => {
      onChange(value);
      telemetry.sortOptionChanged({ sortOption: value });
      if (!isMobileOrTablet) {
        onClose();
      }
    },
    [onChange, telemetry, isMobileOrTablet, onClose],
  );

  const SortContent = (
    <Box
      padding={isMobileOrTablet ? pxToRem(24) : 7}
      minWidth={isMobileOrTablet ? '100%' : pxToRem(380)}
    >
      <SortOptionsFacet
        selectedValue={selectedValue}
        onChange={handleSortChange}
        includeCouplesPicks={includeCouplesPicks}
        includeFundSavings={includeFundSavings}
        accentColor={accentColor}
      />
    </Box>
  );

  const filterButtonIconSize = 16;
  const trigger = (
    <DropdownButton
      text={
        appearance === 'pill' ? (
          <>
            {/* A margin, not a space: the button lays out its content as flex items, which drops a lone space. */}
            <TextV2
              as="span"
              typographyVariant="label2"
              marginRight={2}
            >
              {tq`Sort:`}
            </TextV2>
            {getSelectedOptionLabel(selectedValue)}
          </>
        ) : (
          `${sortTrans.sortPrefix()} ${getSelectedOptionLabel(selectedValue)}`
        )
      }
      endIcon={<ChevronDown size={filterButtonIconSize} />}
      accentColor={accentColor}
      appearance={appearance}
    />
  );

  return (
    <Flex position={isMobileOrTablet ? 'unset' : 'relative'}>
      <Box onClick={onOpen}>{trigger}</Box>
      <PopoverV2
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        placement="bottom"
        overrides={{
          Root: { props: { minWidth: pxToRem(280), top: isMobileOrTablet ? 80 : 8, left: isMobileOrTablet ? -28 : 'none', right: isMobileOrTablet ? 'none' : 0 } },
          Content: {
            props: {
              borderRadius: pxToRem(24),
              style: { userSelect: 'none' },
            },
          },
        }}
      >
        <PopoverV2.Content>{SortContent}</PopoverV2.Content>
      </PopoverV2>
    </Flex>
  );
};
